import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './Dialog.css.ts';

/**
 * Dialogコンポーネントのプロパティ
 */
export interface DialogProps {
  /** ダイアログの表示状態を制御 */
  isOpen: boolean;
  /** ダイアログを閉じる時に呼び出されるコールバック */
  onClose: () => void;
  /** ダイアログのタイトルテキスト */
  title: string;
  /** ダイアログのコンテンツ */
  children: ReactNode;
  /** フッターコンテンツ（通常はアクションボタン）（オプション） */
  footer?: ReactNode;
  /** ダイアログの幅プリセット。デフォルトは'md' */
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fullscreen';
  /** trueの場合、オーバーレイクリックでダイアログを閉じる。デフォルトはtrue */
  closeOnOverlayClick?: boolean;
  /** trueの場合、ヘッダーに閉じるボタンを表示。デフォルトはtrue */
  showCloseButton?: boolean;
  /** 追加のCSSクラス名 */
  className?: string;
  /** テスト用のdata-testid属性 */
  'data-testid'?: string;
}

/**
 * オーバーレイとフォーカス管理を備えたモーダルダイアログコンポーネント
 * Escapeキー、フォーカストラップ、ボディスクロールロックを自動的に処理します。
 * モーダルレイヤーにz-index 10000以上を使用します。
 *
 * @example
 * ```tsx
 * <Dialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirm Action"
 *   footer={
 *     <>
 *       <Button variant="secondary" onClick={() => setIsOpen(false)}>
 *         Cancel
 *       </Button>
 *       <Button variant="primary" onClick={handleConfirm}>
 *         Confirm
 *       </Button>
 *     </>
 *   }
 * >
 *   Are you sure you want to proceed?
 * </Dialog>
 * ```
 */
export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
  'data-testid': dataTestId
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const hasOpenedRef = useRef(false);

  // Escapeキーで閉じる
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // フォーカストラップを設定（最初のopen時のみ）
      if (!hasOpenedRef.current) {
        dialogRef.current?.focus();
        hasOpenedRef.current = true;
      }
    } else {
      hasOpenedRef.current = false;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // 開いているときボディのスクロールを無効化
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      data-testid={dataTestId}
    >
      <div
        ref={dialogRef}
        className={`${styles.dialog({ width })} ${className}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* ヘッダー */}
        <div className={styles.header}>
          <h2 id="dialog-title" className={styles.title}>{title}</h2>
          {showCloseButton && (
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close dialog"
              data-testid="close-button"
            >
              ×
            </button>
          )}
        </div>

        {/* コンテンツ */}
        <div className={styles.content}>
          {children}
        </div>

        {/* フッター（オプション） */}
        {footer && (
          <div className={styles.footer}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};